import type { QueryClient } from "@tanstack/react-query";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { creditKeys } from "@/features/credits/hooks/query-keys";
import { invoiceKeys } from "@/features/invoices/hooks/query-keys";
import { dashboardKeys } from "@/features/dashboard/hooks/query-keys";
import { paymentKeys } from "@/features/customer-payments/hooks/query-keys";

/**
 * Every customer-payment write moves the payment record, its invoice balance,
 * and the cash-in and receivables figures on both dashboards.
 *
 * `invoiceKeys.all` deliberately covers invoice lists and details through
 * TanStack Query's prefix matching. Keeping that rule here means callers do not
 * need to thread an invoice id solely to repeat a narrower invalidation.
 *
 * `creditKeys.all` is included because an overpayment banks the remainder as a
 * `gl_customer_credits` row: recording, correcting, or deleting the payment
 * moves what the account is owed, so a credits surface left on the old cache
 * shows a balance the payment no longer supports.
 */
export async function invalidateCustomerPaymentQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: paymentKeys.customer.all }),
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
    queryClient.invalidateQueries({ queryKey: creditKeys.all }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
    queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
  ]);
}
